import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

type AccentColor = 'purple' | 'emerald';

const ACCENT_CLASSES: Record<AccentColor, {
  bg: string;
  ring: string;
  text: string;
  textFaded: string;
}> = {
  purple: {
    bg: 'bg-threadheads-purple',
    ring: 'ring-threadheads-purple/30',
    text: 'text-threadheads-purple',
    textFaded: 'text-threadheads-purple/70',
  },
  emerald: {
    bg: 'bg-emerald-600',
    ring: 'ring-emerald-600/30',
    text: 'text-emerald-600',
    textFaded: 'text-emerald-600/70',
  },
};

interface StepIndicatorProps {
  steps: { key: string; label: string }[];
  currentStep: string;
  accentColor?: AccentColor;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  accentColor = 'purple',
}) => {
  const current = steps.findIndex((s) => s.key === currentStep);
  const colors = ACCENT_CLASSES[accentColor];

  return (
    <div className="flex items-center justify-between w-full px-2">
      {steps.map((step, i) => {
        const isCompleted = i < current;
        const isActive = i === current;

        return (
          <React.Fragment key={step.key}>
            {/* Connector line */}
            {i > 0 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-1',
                  i <= current ? colors.bg : 'bg-muted'
                )}
              />
            )}

            {/* Step dot + label */}
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div
                className={cn(
                  'flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium transition-colors',
                  isCompleted &&
                    `${colors.bg} text-white`,
                  isActive &&
                    `${colors.bg} text-white ring-2 ${colors.ring}`,
                  !isCompleted &&
                    !isActive &&
                    'bg-muted text-muted-foreground'
                )}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={cn(
                  'text-[11px] leading-tight',
                  isActive
                    ? `${colors.text} font-medium`
                    : isCompleted
                      ? colors.textFaded
                      : 'text-muted-foreground'
                )}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepIndicator;
